import { ChanceStatus } from '../../ts-config';

/**
 * @description 对比接口返回的机会点数据
 * 除固定字段外，其余字段均为接口按需返回的指标字段
 */
export interface ComparePoint {
  pointId: number;
  name: string;
  number?: string;
  status?: string;
  statusName?: string;
  shopName?: string;
  [key: string]: any;
}

/**
 * @description 对比指标值的展示类型
 * text：文本；number：普通数值；percent：百分比；sales：销售额（元转万元）；integer：整数
 */
export type CompareFieldKind = 'text' | 'number' | 'percent' | 'sales' | 'integer';

/**
 * @description 对比指标配置
 * best 为最优方向，仅在需要高亮的字段上配置
 */
export interface CompareField {
  key: string;
  label: string;
  unit?: string;
  kind?: CompareFieldKind;
  best?: 'min' | 'max';
  description?: string;
}

/** 最多参与对比的点位数（含基准点位） */
export const COMPARE_MAX_COUNT = 5;

/** 对比列头部的装饰色，按点位顺序循环使用 */
export const COMPARE_COLORS = ['#24B4FF', '#38CDB3', '#5D88FF', '#6BCEFF', '#AA81FF'];

/**
 * @description 机会点状态颜色
 * 与移动端机会点列表 getStatusColor 的分组保持一致，不使用 PC 端旧的颜色映射
 */
export const COMPARE_STATUS_COLOR: Record<number, string> = {
  [ChanceStatus.Draft]: '#FF861D',
  [ChanceStatus.WaitApprove]: '#FF861D',
  [ChanceStatus.WaitReport]: '#FF861D',
  [ChanceStatus.Approving]: '#207FFF',
  [ChanceStatus.Estimating]: '#207FFF',
  [ChanceStatus.Signing]: '#207FFF',
  [ChanceStatus.Pass]: '#06C282',
  [ChanceStatus.Estimated]: '#06C282',
  [ChanceStatus.Complete]: '#06C282',
  [ChanceStatus.Reject]: '#F53F3F',
  [ChanceStatus.EstimateFail]: '#F53F3F',
  [ChanceStatus.Close]: '#999999',
};

/**
 * @description 对比表格指标行配置
 * 顺序与接口文档字段顺序保持一致：8 个测算指标 + 16 个决策报告指标
 */
export const COMPARE_FIELDS: CompareField[] = [
  {
    key: 'cpmMS',
    label: 'CPM',
    unit: '元/千次',
    kind: 'number',
    best: 'min',
    description: 'CPM = 月度租金及费用 ÷ 日均过店客流 × 1000，即每千人次过店客流的获取成本，越低说明点位性价比越高',
  },
  {
    key: 'paybackMonthsMS',
    label: '回本周期',
    unit: '个月',
    kind: 'number',
    best: 'min',
    description: '回本周期 = 一次性投入 ÷ 月净利润；一次性投入不含押金与开业备用金，与开店成本口径不同',
  },
  {
    key: 'rentSalesRatioMS',
    label: '租售比',
    unit: '%',
    kind: 'percent',
    best: 'min',
    description: '租售比 = 月度租金及费用 ÷ 月预估销售额 × 100%',
  },
  {
    key: 'netMarginMS',
    label: '净利率',
    unit: '%',
    kind: 'percent',
    best: 'max',
    description: '净利率 = 月净利润 ÷ 月预估销售额 × 100%',
  },
  {
    key: 'monthlySalesEstMS',
    label: '月预估销售额',
    unit: '万元',
    kind: 'sales',
    best: 'max',
    description: '月预估销售额 = 过店客流 × 进店率 × 转化率 × 客单价 × 30',
  },
  {
    key: 'dailyTrafficMS',
    label: '日均过店客流',
    unit: '',
    kind: 'integer',
    best: 'max',
    description: '数据来自实踩客流加权聚合',
  },
  {
    key: 'monthlyRentAndFees',
    label: '月度租金及费用',
    unit: '元',
    kind: 'number',
    best: 'min',
    description: '月度租金及费用 = 实付租金 + 物业费 + 推广基金；两者取高模式下，实付租金 = MAX（保底租金，月预估销售额 × 扣点比例）',
  },
  {
    key: 'synergyCoefMS',
    label: '协同系数',
    unit: '',
    kind: 'number',
    best: 'max',
    description: '协同系数 = 楼层基础系数 × 品牌综合系数，由本体模型输出',
  },
  { key: 'builtArea', label: '面积', unit: '㎡', kind: 'number' },
  { key: 'basicMallStoreyHeight', label: '层高', unit: 'm', kind: 'number' },
  { key: 'groundFloor', label: '楼层', kind: 'text' },
  { key: 'businessTerms', label: '商务条件', kind: 'text' },
  { key: 'propertyManagementMS', label: '物业费', unit: '元', kind: 'number' },
  { key: 'promotioncostsMS', label: '推广费', unit: '元', kind: 'number' },
  { key: 'unloadingZone', label: '点位备注', kind: 'text' },
  { key: 'businessLeaseTermMS', label: '租期', unit: '个月', kind: 'integer' },
  { key: 'depositMonthsMS', label: '押金月数', unit: '个月', kind: 'integer' },
  { key: 'decorationDepositMS', label: '装修押金', unit: '元', kind: 'number' },
  { key: 'goodsCostMS', label: '首批货品', unit: '元', kind: 'number' },
  { key: 'goodsCostRateMS', label: '货品成本率', unit: '%', kind: 'percent' },
  { key: 'openingReserveFundMS', label: '开业备用金', unit: '元', kind: 'number' },
  { key: 'exhibitionDesignFeeMS', label: '展具设计', unit: '元', kind: 'number' },
  { key: 'constructionFeeMS', label: '运输搭建', unit: '元', kind: 'number' },
  { key: 'laborCostsMS', label: '人力成本', unit: '元', kind: 'number' },
];

/** 综合数据排序卡片字段 */
export interface CompareRankField {
  key: string;
  label: string;
  /** 金额类字段：>10000 转万，<=10000 展示原值，且不展示“元” */
  money?: boolean;
  /** 非金额字段的单位 */
  unit?: string;
}

/** 综合数据排序卡片字段，顺序与设计稿一致 */
export const COMPARE_RANK_FIELDS: CompareRankField[] = [
  { key: 'dailyTrafficMS', label: '日均过店客流' },
  { key: 'monthlySalesEstMS', label: '月预估销售额', money: true },
  { key: 'monthlyRentAndFees', label: '月租金及费用', money: true },
  { key: 'cpmMS', label: 'CPM', money: true },
  { key: 'paybackMonthsMS', label: '回本周期', unit: '个月' },
  { key: 'rentSalesRatioMS', label: '租售比', unit: '%' },
  { key: 'netMarginMS', label: '净利率', unit: '%' },
];
